// Layer 3 (Transformation): roster-derived period windows for persona landings
// (vision-run roster-period R1, #285).

import type { PlantRoleId } from '@/versions/v1/schema/plant-role';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import type { CrewId } from '@/versions/v1/schema/seat';
import { PERSONA_CREW, rosterShiftAt } from '@/versions/v1/shared/bunker-hill-org-seed';
import {
  addMinutes,
  asCaseTime,
  clockOnProductionDay,
  diffMinutes,
  formatCursorBound,
  previousShiftWindow,
  productionDayOf,
  shiftOf,
  type ShiftWindow,
} from '@/versions/v1/shared/clock.derive';
import { SERIES_BY_READING_ID } from '@/versions/v1/shared/bunker-hill-series';
import { seriesAt } from '@/versions/v1/shared/series.derive';
import type { SessionState } from '@/versions/v1/schema/scenario';

export type RosterPeriodId = 'last-shift' | 'since-last' | 'seven-days';

export type RosterPeriod = {
  id: RosterPeriodId;
  label: string;
  window: ShiftWindow;
  days: number;
};

/** [syn] Metallurgist day-staff 4-on/4-off from 2026-05-24 (day shifts only). */
const MET_DAY_PATTERN_EPOCH = '2026-05-24';
const MET_ON_DAYS = 4;
const MET_CYCLE_DAYS = 8;

function metOnProductionDay(date: string): boolean {
  const start = `${MET_DAY_PATTERN_EPOCH}T06:00` as SeriesTime;
  const at = `${date}T06:00` as SeriesTime;
  const days = Math.floor(diffMinutes(at, start) / (24 * 60));
  const dayInCycle = ((days % MET_CYCLE_DAYS) + MET_CYCLE_DAYS) % MET_CYCLE_DAYS;
  return dayInCycle < MET_ON_DAYS;
}

function crewForShift(at: SeriesTime, kind: 'day' | 'night'): CrewId {
  const roster = rosterShiftAt(at);
  return kind === 'day' ? roster.dayCrewId : roster.nightCrewId;
}

function blockEndForCrewShift(shiftStart: SeriesTime, crewId: CrewId): SeriesTime {
  let end = shiftOf(shiftStart).end;
  let forward = addMinutes(end, 1);
  for (let i = 0; i < 8; i++) {
    const next = shiftOf(forward);
    if (crewForShift(next.start, next.kind) !== crewId) break;
    end = next.end;
    forward = addMinutes(end, 1);
  }
  return end;
}

export function lastCrewBlockEnd(crewId: CrewId, cursor: SeriesTime): SeriesTime {
  const current = shiftOf(cursor);
  let probe: SeriesTime = cursor;

  if (crewForShift(cursor, current.kind) === crewId) {
    probe = addMinutes(current.start, -1);
    while (crewForShift(shiftOf(probe).start, shiftOf(probe).kind) === crewId) {
      probe = addMinutes(shiftOf(probe).start, -1);
    }
  }

  for (let i = 0; i < 60; i++) {
    const shift = shiftOf(probe);
    if (crewForShift(shift.start, shift.kind) === crewId) {
      let blockStart = shift.start;
      let back = addMinutes(shift.start, -1);
      while (crewForShift(shiftOf(back).start, shiftOf(back).kind) === crewId) {
        blockStart = shiftOf(back).start;
        back = addMinutes(blockStart, -1);
      }
      return blockEndForCrewShift(blockStart, crewId);
    }
    probe = addMinutes(shift.start, -1);
  }
  return previousShiftWindow(cursor).start;
}

export function lastMetDayBlockEnd(cursor: SeriesTime): SeriesTime {
  const onNow = metOnProductionDay(productionDayOf(cursor).date);
  let probe: SeriesTime = onNow ? addMinutes(shiftOf(cursor).start, -1) : cursor;

  for (let i = 0; i < 60; i++) {
    const { date } = productionDayOf(probe);
    if (metOnProductionDay(date)) {
      let endDate = date;
      let next = addMinutes(`${date}T18:00`, 24 * 60);
      for (let j = 0; j < MET_ON_DAYS; j++) {
        const nextDate = productionDayOf(next).date;
        if (!metOnProductionDay(nextDate)) break;
        endDate = nextDate;
        next = addMinutes(`${endDate}T18:00`, 24 * 60);
      }
      return `${endDate}T18:00`;
    }
    probe = addMinutes(`${date}T00:00`, -24 * 60);
  }
  return previousShiftWindow(cursor).start;
}

function lastWorkedShiftEnd(personaRole: PlantRoleId, cursor: SeriesTime): SeriesTime {
  if (personaRole === 'cro') {
    const crewId = PERSONA_CREW.cro;
    if (crewId) return lastCrewBlockEnd(crewId, cursor);
  }
  if (personaRole === 'metallurgist') {
    return lastMetDayBlockEnd(cursor);
  }
  return previousShiftWindow(cursor).start;
}

function windowEndingAtCursor(window: ShiftWindow, cursor: SeriesTime): ShiftWindow {
  return { ...window, end: cursor };
}

function sevenProductionDaysWindow(cursor: SeriesTime): ShiftWindow {
  let prod = productionDayOf(cursor);
  let start = prod.start;
  for (let i = 0; i < 6; i++) {
    const prevInstant = addMinutes(prod.start, -1);
    prod = productionDayOf(prevInstant);
    start = prod.start;
  }
  const { kind } = shiftOf(cursor);
  return {
    kind,
    start,
    end: cursor,
    label: '7 days',
  };
}

/** True span in days — unrounded hours / 24 (F1). */
export function windowSpanDays(window: ShiftWindow, cursor: SeriesTime): number {
  return diffMinutes(cursor, window.start) / 60 / 24;
}

function daysSpanLabel(window: ShiftWindow, cursor: SeriesTime): number {
  const days = windowSpanDays(window, cursor);
  return Math.max(1, Math.round(days));
}

function sinceLastWindow(personaRole: PlantRoleId, cursor: SeriesTime): ShiftWindow {
  const start = lastWorkedShiftEnd(personaRole, cursor);
  const days = daysSpanLabel({ start, end: cursor, kind: 'day', label: '' }, cursor);
  return {
    kind: shiftOf(cursor).kind,
    start,
    end: cursor,
    label: `Since my last shift · ${days} d`,
  };
}

/** Three roster-derived windows for CRO and Metallurgist landings (R1). */
export function rosterPeriods(personaRole: PlantRoleId, cursor: SeriesTime): readonly RosterPeriod[] {
  const lastShift = windowEndingAtCursor(previousShiftWindow(cursor), cursor);
  const sinceLast = sinceLastWindow(personaRole, cursor);
  const sevenDays = windowEndingAtCursor(sevenProductionDaysWindow(cursor), cursor);

  const raw: RosterPeriod[] = [
    {
      id: 'last-shift',
      label: 'Last shift',
      window: lastShift,
      days: 1,
    },
    {
      id: 'since-last',
      label: sinceLast.label,
      window: sinceLast,
      days: daysSpanLabel(sinceLast, cursor),
    },
    {
      id: 'seven-days',
      label: '7 days',
      window: sevenDays,
      days: 7,
    },
  ];

  const seen = new Set<string>();
  return raw.filter((period) => {
    if (seen.has(period.window.start)) return false;
    seen.add(period.window.start);
    return true;
  });
}

export function defaultRosterPeriod(personaRole: PlantRoleId, cursor: SeriesTime): RosterPeriod {
  const periods = rosterPeriods(personaRole, cursor);
  return (
    periods.find((row) => row.id === 'since-last') ??
    periods.find((row) => row.id === 'last-shift') ??
    periods[0]!
  );
}

/** Whether a stamp falls inside a period window (end inclusive when it equals the cursor). */
export function windowContains(
  stamp: string,
  cursor: SeriesTime,
  window: ShiftWindow,
): boolean {
  const full = stamp.includes('T') ? asCaseTime(stamp) : clockOnProductionDay(stamp, cursor);
  if (diffMinutes(full, window.start) < 0) return false;
  if (diffMinutes(window.end, cursor) === 0) {
    return diffMinutes(full, window.end) <= 0;
  }
  return diffMinutes(full, window.end) < 0;
}

/** Compact window footnote — `since 04 Jun 18:00`. */
export function windowSinceLabel(window: ShiftWindow): string {
  return `since ${formatCursorBound(window.start)}`;
}

const CRO_SAMPLE_READING_IDS = [
  'rd-kpi-pb-rec',
  'rd-kpi-zn-rec',
  'rd-kpi-ag-rec',
  'rd-kpi-pb-grade',
  'rd-kpi-zn-grade',
  'rd-kpi-ag-grade',
  'rd-head-pb',
  'rd-head-zn',
  'rd-head-ag',
] as const;

const MET_SAMPLE_READING_IDS = [
  ...CRO_SAMPLE_READING_IDS,
  'rd-reag-znso4',
  'rd-reag-smbs',
  'rd-reag-cuso4',
  'rd-reag-mibc',
  'rd-reag-3418a',
  'rd-reag-aero',
  'rd-reag-lime',
  'rd-pb-rgh-con-pb',
  'rd-zn-rgh-con-zn',
] as const;

export function sampleReadingIdsForRole(role: PlantRoleId): readonly string[] {
  if (role === 'metallurgist') return MET_SAMPLE_READING_IDS;
  if (role === 'cro') return CRO_SAMPLE_READING_IDS;
  return CRO_SAMPLE_READING_IDS;
}

function shiftPointsInWindow(readingId: string, state: SessionState, window: ShiftWindow): number {
  const series = SERIES_BY_READING_ID[readingId];
  if (!series || series.cadence !== 'shift') return 0;
  const view = seriesAt(series.id, state);
  let count = 0;
  for (const point of view.points) {
    if (point.value === null || point.value === undefined) continue;
    if (windowContains(point.at, state.cursor, window)) count += 1;
  }
  return count;
}

/** Shift-cadence assay count for a persona landing window (R5). */
export function sampleCountRead(
  state: SessionState,
  window: ShiftWindow,
  role: PlantRoleId,
  periodId?: RosterPeriodId,
): { assays: number; perDay: number; spanDays: number; label: string } {
  const cursor = state.cursor;
  const readingIds = sampleReadingIdsForRole(role);
  let assays = 0;
  const seen = new Set<string>();
  for (const readingId of readingIds) {
    const series = SERIES_BY_READING_ID[readingId];
    if (!series || series.cadence !== 'shift') continue;
    if (seen.has(series.id)) continue;
    seen.add(series.id);
    assays += shiftPointsInWindow(readingId, state, window);
  }
  const spanDays = windowSpanDays(window, cursor);
  const isLastShift = periodId === 'last-shift';
  if (isLastShift) {
    return {
      assays,
      perDay: assays,
      spanDays,
      label: `${assays} assays · this shift`,
    };
  }
  const perDay = spanDays > 0 ? Math.round((assays / spanDays) * 10) / 10 : assays;
  return {
    assays,
    perDay,
    spanDays,
    label: `${assays} assays · ${perDay}/day`,
  };
}

export function rosterPatternTooltip(
  personaRole: PlantRoleId,
  periods?: readonly RosterPeriod[],
): string {
  if (personaRole === 'cro') {
    if (periods && !periods.some((row) => row.id === 'since-last')) {
      return '[syn] C Crew · day 2 of 2 · since-last equals last shift';
    }
    return '[syn] C Crew on a four-crew 2-2 day/night rotation from 24 May 2026';
  }
  if (personaRole === 'metallurgist') {
    return '[syn] Day-staff 4-on/4-off pattern from 24 May 2026';
  }
  return '[syn] Plant roster';
}
